import { useCallback, useEffect, useRef } from "react";

import { useAuth } from "../core/auth/AuthContext";
import { useAuthUI } from "../core/auth/AuthUIContext";
import { useKeyboardAwareOffset } from "../hooks/useKeyboardAwareOffset";
import { useModalAccessibility } from "../hooks/useModalAccessibility";
import QuickAuthPanel from "./auth/QuickAuthPanel";
import styles from "./AuthModal.module.css";

// Fenetre de connexion ouverte depuis l'appli (participation, messages...) :
// un en-tete selon le contexte et le meme parcours court que l'ecran de
// connexion (QuickAuthPanel).
export default function AuthModal() {
  const keyboardOffset = useKeyboardAwareOffset();
  const dialogRef = useRef(null);
  useModalAccessibility(dialogRef);
  const { closeAuthModal, consumeAuthIntent, pendingAuthIntent } = useAuthUI();
  const { loadingAction, isAuthenticated, clearAuthError } = useAuth();

  const isJoinIntent = ["join_event", "reserve_event_badge"].includes(pendingAuthIntent?.type);
  const title = isJoinIntent
    ? pendingAuthIntent?.type === "reserve_event_badge"
      ? "Réserve ta place"
      : "Rejoins cette sortie"
    : "Bienvenue sur Karango";
  const intro = isJoinIntent
    ? "Connecte-toi ou crée ton compte. Tu reviendras ensuite directement à cette sortie."
    : "Connecte-toi ou crée ton compte pour retrouver tes sorties.";

  const handleClose = useCallback(() => {
    if (loadingAction) return;
    clearAuthError();
    consumeAuthIntent();
    closeAuthModal();
  }, [clearAuthError, closeAuthModal, consumeAuthIntent, loadingAction]);

  // Connecte : l'intention (ex. participation) est gardee pour reprendre
  // l'action la ou elle s'etait arretee.
  useEffect(() => {
    if (isAuthenticated) closeAuthModal();
  }, [closeAuthModal, isAuthenticated]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  return (
    <div className={styles.overlay} style={{ bottom: keyboardOffset }} onClick={handleClose} role="presentation">
      <div
        ref={dialogRef}
        className={styles.card}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.topBar}>
          <span className={styles.brand}>KARANGO</span>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            disabled={loadingAction}
            aria-label="Fermer"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <header className={styles.header}>
          <h2 id="auth-modal-title" className={styles.title}>{title}</h2>
          <p className={styles.body}>{intro}</p>
        </header>

        <QuickAuthPanel onAuthenticated={closeAuthModal} />

        <p className={styles.providerTerms}>
          En continuant, tu acceptes les <a href="/terms.html" target="_blank" rel="noreferrer">conditions d’utilisation</a> et la <a href="/privacy.html" target="_blank" rel="noreferrer">politique de confidentialité</a>.
        </p>

        <button
          type="button"
          className={styles.continueGuestButton}
          onClick={handleClose}
          disabled={loadingAction}
        >
          Continuer sans compte
        </button>
      </div>
    </div>
  );
}
